import React, { useState, useMemo, useEffect } from "react";
import { Table, Typography } from "antd";
import { Filters } from "../../types/dashboard/filters";
import { stageData, StageKey } from "../../types/dashboard/stageData";
import "../../style/dashboard/ListingRowColumn.css"
import { Tooltip, Space, message } from "antd";
import { EyeOutlined, EyeInvisibleOutlined, CopyOutlined } from "@ant-design/icons";

interface JoiningsTableProps {
  data: any[];
  filter: Filters;
  userName: string;
  currentUserRole?: string;
  menuName?: string;
  onRowClick?: (row: any) => void;
  defaultValue?: string; // optional default value for empty cells
}

const { Title } = Typography;

const maskMobile = (mobile?: string) => {
  if (!mobile) return "--";
   
  return mobile.replace(/\d(?=\d{4})/g, "*");
};

// Copy helper
const copyToClipboard = async (text: string, label: string) => {
  try {
    await navigator.clipboard.writeText(text);
    message.success(`${label} copied`);
  } catch {
    message.error("Failed to copy");
  }
};

const JoiningsTable: React.FC<JoiningsTableProps> = ({
  data,
  filter,
  userName,
  currentUserRole,
  menuName,
  onRowClick,
  defaultValue = "--", // default fallback if value is missing
}) => {
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100, pageSizeOptions: ["10", "20", "50", "100"], showSizeChanger: true, });
  const [sorter, setSorter] = useState<{ field?: string; order?: string }>({});

  const handleTableChange = (pager: any, _: any, sorter: any) => {
    setPagination(pager);
    setSorter({ field: sorter.field, order: sorter.order });
  };

//const userName =  localStorage.getItem('userName') || '';
//const CAN_VIEW_MOBILE_ROLES = ["ADMIN", "MANAGER", "VIEWER"];
const AUTO_HIDE_TIME = 10000; // 10 seconds
//const [visibleMobiles, setVisibleMobiles] = useState<Record<string, boolean>>({});
const hideTimers = React.useRef<Record<string, NodeJS.Timeout>>({});

  // Reset pagination on filter change
  useEffect(() => {
    setPagination({ current: 1, pageSize: 10, pageSizeOptions: ["10", "20", "50", "100"], showSizeChanger: true, });
  }, [filter]);

  //const isMobile = window.innerWidth <= 576;

  const [visibleMobiles, setVisibleMobiles] = useState<Record<string, boolean>>({});

  const toggleMobileVisibility = (id: string) => {
    setVisibleMobiles((prev) => {
      const next = !prev[id];

      // clear previous timer
      if (hideTimers.current[id]) {
        clearTimeout(hideTimers.current[id]);
      }

      // auto-hide after X seconds
      if (next) {
        hideTimers.current[id] = setTimeout(() => {
          setVisibleMobiles((p) => ({ ...p, [id]: false }));
        }, AUTO_HIDE_TIME);
      }

      return { ...prev, [id]: next };
    });
  };

  const clickableCell = (record: any) => ({
    onClick: () => onRowClick?.(record),
    style: { cursor: "pointer" },
  });

  // Client-side sorting
  const sortedData = useMemo(() => {
    const field = sorter.field;
    const order = sorter.order;
    if (!field || !order) return data;

    const sorted = [...data].sort((a, b) => {
      const valA = a[field as keyof typeof a];
      const valB = b[field as keyof typeof b];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "number" && typeof valB === "number") {
        return valA - valB;
      }

      return String(valA).localeCompare(String(valB));
    });

    if (order === "descend") {
      sorted.reverse();
    }

    return sorted;
  }, [data, sorter]);

  const columns = [
    {
      title: () => <strong>#</strong>,
      key: "index",
      width: 40,
      align: "center" as "center",
      onCell: clickableCell,
      render: (_text: any, _record: any, index: number) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    }, {
      title: () => <strong>Name</strong>,
      dataIndex: "name",
      key: "name",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    }, {
      title: () => <strong>Mobile Number</strong>,
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      sorter: true,
      onCell: clickableCell,
      render: (value: string, record: any) => {
        const normalize = (v?: string) => v?.trim().toUpperCase();

      const assignedTo = normalize(record.assignTo);
      const currentUser = normalize(userName);

      const isOwner =
        assignedTo === currentUser || currentUserRole === "ADMIN";

        const isVisible = visibleMobiles[record.id];
        //const canView = CAN_VIEW_MOBILE_ROLES.includes(currentUserRole || "") && isOwner;

        return (
          <Space>
            <span>
              {(isVisible) ? value : maskMobile(value)}
            </span>
            {(isOwner) && (
              <>
                <Tooltip title={isVisible ? "Hide (auto in 10s)" : "Show"}> <span> {isVisible ? <EyeInvisibleOutlined onClick={(e) => { e.stopPropagation(); toggleMobileVisibility(record.id); }} />: <EyeOutlined onClick={(e) => { e.stopPropagation(); toggleMobileVisibility(record.id); }} />} </span> </Tooltip>
                <Tooltip title="Copy Mobile number"><span><CopyOutlined onClick={(e) => { e.stopPropagation(); copyToClipboard(value, "Mobile number"); }} /></span> </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
    // Conditionally render "Assigned To"
   // ...(currentUserRole === "ADMIN" || currentUserRole === "MANAGER"
    //  ? [
          {
            title: () => <strong>Assigned To</strong>,
            dataIndex: "assignTo",
            key: "assignTo",
            sorter: true,
            onCell: clickableCell,
            render: (value: any) => value ?? defaultValue,
          },
   //     ]
    //  : []),
    {
      title: () => <strong>Follow-Up</strong>,
      dataIndex: "stage",
      key: "stage",
      sorter: true,
      onCell: clickableCell,
      render: (_value: any, record: any) =>
      stageData[record.stage as StageKey]?.label || record.followupStatus || defaultValue,
    }, {
      title: () => <strong>City</strong>,
      dataIndex: "city",
      key: "city",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    }, {
      title: () => <strong>Status</strong>,
      dataIndex: "status",
      key: "status",
      sorter: true,
      onCell: clickableCell,
      render: (_value: any, record: any) =>
      stageData[record.stage as StageKey]?.subdivisions.find(
        (s) => s.value === record.followupStatus
      )?.label || record.followupStatus || defaultValue,
    }, {
      title: () => <strong>Notes</strong>,
      dataIndex: "notes",
      key: "notes",
      sorter: true,
      onCell: clickableCell,
      render: (value: any) => value ?? defaultValue,
    },
  ];
  

  return (
    <div className="table-wrapper">
      {menuName && (
        <div className="table-title-header">
          <Title level={4} style={{ margin: 0 }}>
            {menuName}
          </Title>
        </div>
      )}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={sortedData}
        pagination={{
          ...pagination,
          total: sortedData.length,
          position: ["bottomCenter"],
          showSizeChanger: true, 
          showQuickJumper: true,     // hide quick jumper on mobile
          
          //showTotal: (t) => `Total ${t} items`,
        }}
        sticky={{ offsetHeader: 0 }}      // ✅ Correct sticky header
        onChange={handleTableChange}
        rowClassName={(_, index) => (index % 2 === 0 ? "even-row" : "odd-row")}
        size="small"
        scroll={{
          x: "max-content",  // 🔥 horizontal scroll for mobile
          y: 412,             // existing vertical scroll
          scrollToFirstRowOnChange: true
        }}
      />
    </div>

  );
};

export default JoiningsTable;
